import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/*
 * The one place a route chunk is loaded.
 *
 * A route split with `React.lazy` has a failure mode with no user-visible
 * output at all: when the chunk cannot be fetched the loader's promise rejects,
 * React unwinds past the Suspense boundary, and with nothing to catch the
 * rejection the whole tree is torn down. The DOM that remains is an empty
 * <main>. Measured on production on 2026-08-12: a deep link or a hard refresh
 * opened a blank screen with one console line —
 *
 *   TypeError: Failed to fetch dynamically imported module: .../IntegrationPage-*.js
 *
 * — and nothing on the page. The file was served correctly (200, right MIME,
 * valid ESM, `curl` instant every time); the request itself hung. It was never
 * the same screen twice and never reproduced through in-app navigation, only on
 * the cold load, where a burst of chunk requests goes out at once.
 *
 * So the retry has to live HERE, inside the loader, and not in the boundary.
 * `React.lazy` remembers a rejection permanently: once its internal state is
 * Rejected it re-throws the stored error on every subsequent render, so a
 * boundary that "resets" would hand the same component back and be thrown at
 * again. Anything that wants a second attempt must make it before React sees
 * the first failure.
 */

const ATTEMPTS = 3;
/**
 * A short pause, then a longer one. Long enough for a stalled connection to be
 * torn down and reopened, short enough that the total worst case stays under a
 * second and a half — past that a person has already decided the app is broken.
 */
const backoffMs = (attempt: number) => (attempt === 1 ? 250 : 750);

/**
 * The four ways a browser says "I could not fetch that module".
 *
 * Matched on the message because there is no error TYPE to match on: every
 * engine throws a plain TypeError. The list is deliberately narrow — an error
 * that is not one of these came from the module's own top-level code, and
 * re-running that three times would only run a bug three times.
 */
const NETWORK_FAILURE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i;

/** The chunk URL Chrome and Firefox put in the message. Safari omits it. */
const URL_IN_MESSAGE = /\bhttps?:\/\/[^\s'"]+?\.m?js\b/i;

/**
 * Raised after the last attempt, so the boundary can tell "the network never
 * delivered this screen" apart from "this screen threw while rendering". The
 * two need different words: one is fixed by trying again, the other is not.
 */
export class ChunkLoadError extends Error {
  constructor(readonly cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'ChunkLoadError';
  }
}

export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof ChunkLoadError) return true;
  return error instanceof Error && NETWORK_FAILURE.test(error.message);
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retry against a URL the module map has not already given up on.
 *
 * Per spec a failed module fetch is recorded in the document's module map, and
 * engines differ on whether a later import of the same specifier gets a fresh
 * attempt or the remembered failure. A distinct URL is not subject to that
 * question, so the retry appends a query parameter: Caddy's file_server ignores
 * it, `immutable` caching cannot answer it from a poisoned entry, and the
 * chunk's own imports are relative and still resolve to the canonical paths —
 * so React and the vendor chunks are not loaded a second time.
 *
 * Safari puts no URL in the message. There the plain loader is re-run, which is
 * the best available and still correct.
 */
function retryImport<M>(load: () => Promise<M>, error: unknown, attempt: number): Promise<M> {
  const found = error instanceof Error ? URL_IN_MESSAGE.exec(error.message) : null;
  if (!found) return load();

  const url = new URL(found[0], window.location.href);
  url.searchParams.set('gh-retry', String(attempt));
  return import(/* @vite-ignore */ url.href) as Promise<M>;
}

async function importWithRetry<M>(load: () => Promise<M>): Promise<M> {
  let lastError: unknown;

  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    if (attempt > 0) await sleep(backoffMs(attempt));
    try {
      return attempt === 0 ? await load() : await retryImport(load, lastError, attempt);
    } catch (error) {
      // Only a fetch failure is worth repeating. A module that loaded and threw
      // is a bug, and it must reach the boundary on the first throw.
      if (!isChunkLoadError(error)) throw error;
      lastError = error;
    }
  }

  throw new ChunkLoadError(lastError);
}

/**
 * `React.lazy` for a route, with the retry attached.
 *
 * Two arguments rather than the usual single thunk: the retry needs the raw
 * module namespace so it can re-import a cache-busted URL and still pick the
 * same export. `lazy(() => import(x).then((m) => ({ default: m.X })))` folds
 * the pick into the thunk and leaves nothing to re-run.
 *
 *   const AuditPage = lazyRoute(
 *     () => import('@/features/audit/AuditPage'),
 *     (m) => m.AuditPage,
 *   );
 *
 * Every route goes through this. `scripts/check-lazy-routes.mjs` fails CI if
 * any file outside this one imports `lazy` from react, because a route that
 * skips the wrapper is a blank screen nobody discovers until production — and
 * remembering to wrap the next one added is exactly the discipline that fails.
 */
export function lazyRoute<M>(
  load: () => Promise<M>,
  pick: (module: M) => ComponentType,
): LazyExoticComponent<ComponentType> {
  return lazy(async () => ({ default: pick(await importWithRetry(load)) }));
}
